import {
  alpha,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Fab,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { AccountCircle, Close, Delete, Edit } from "@mui/icons-material";
import { IProfile } from "pages/Profile";
import { SubmitHandler, useForm } from "react-hook-form";
import { stringAvatar } from "utils";
import { updateProfile as updateProfileApi } from "apis";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { fetchProfile } from "store/features/profile";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  item: IProfile;
}

const ProfileDialog: React.FC<Props> = ({ open, onClose, item }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { register, handleSubmit, setValue } = useForm<IProfile>({
    defaultValues: item,
  });
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [photo, setPhoto] = useState<File>();
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setValue("photo", photo);
  }, [photo, setValue]);

  const onSubmit: SubmitHandler<IProfile> = (data) => {
    setSubmitting(true);
    updateProfileApi(data)
      .then(({ data }) => {
        dispatch(fetchProfile(data));
        onClose();
        enqueueSnackbar(t("profile.updated"), { variant: "success" });
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Failed", {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  const handleRemovePhoto = () => {
    setPhoto(undefined);
    setPhotoRemoved(true);
    setValue("photo", undefined);
    setValue("removePhoto", true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <AccountCircle sx={{ fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700}>
            {t("profile.edit")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* Photo */}
          <Box display="flex" justifyContent="center" mb={3}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Box display="flex" gap={0.5}>
                  <Fab
                    size="small"
                    component="label"
                    color="primary"
                    sx={{ width: 28, height: 28, minHeight: 28 }}
                  >
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          setPhoto(e.target.files[0]);
                          setPhotoRemoved(false);
                        }
                      }}
                    />
                    <Edit sx={{ fontSize: 14 }} />
                  </Fab>
                  {(photo || (!photoRemoved && item.photo)) && (
                    <Fab
                      size="small"
                      color="error"
                      onClick={handleRemovePhoto}
                      sx={{ width: 28, height: 28, minHeight: 28 }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </Fab>
                  )}
                </Box>
              }
            >
              <Avatar
                src={
                  photo
                    ? URL.createObjectURL(photo)
                    : !photoRemoved && item.photo
                      ? (item.photo as string)
                      : undefined
                }
                {...(!photo && (photoRemoved || !item.photo)
                  ? stringAvatar(`${item.firstName} ${item.lastName}`, {
                      width: 80,
                      height: 80,
                      fontSize: 28,
                      border: `3px solid ${theme.palette.divider}`,
                    })
                  : {
                      sx: {
                        width: 80,
                        height: 80,
                        fontSize: 28,
                        border: `3px solid ${theme.palette.divider}`,
                      },
                    })}
              />
            </Badge>
          </Box>

          {/* Fields */}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {t("labels.general_info")}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.first_name")}
                fullWidth
                size="small"
                {...register("firstName")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.last_name")}
                fullWidth
                size="small"
                {...register("lastName")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("inputs.email")}
                fullWidth
                size="small"
                {...register("email")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("inputs.mobile")}
                fullWidth
                size="small"
                {...register("mobile")}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {t("address")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={t("inputs.address_line1")}
                fullWidth
                size="small"
                {...register("address.line1")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("inputs.address_line2")}
                fullWidth
                size="small"
                {...register("address.line2")}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("inputs.city")}
                fullWidth
                size="small"
                {...register("address.city")}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("inputs.post_code")}
                fullWidth
                size="small"
                {...register("address.postcode")}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t("inputs.country")}
                fullWidth
                size="small"
                {...register("address.country")}
              />
            </Grid>
          </Grid>
        </DialogContent>

        {/* Actions */}
        <Divider />
        <Box sx={{ px: 3, py: 2, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            sx={{ flex: 1, color: "text.secondary", borderColor: "divider" }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ flex: 2 }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.save")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default ProfileDialog;
